import React from 'react';
import { Button, Modal, Form, Input, Table, Tag, Typography, Alert, notification, Space } from 'antd';
import { FilePdfOutlined, MailOutlined } from '@ant-design/icons';
import moment from 'moment';

import request from '../lib/request';

/** Fetches the PDF with the auth header and hands it to the browser (open in a tab, or save). */
export function fetchDocumentPdf(type, id, { open = true, fileName } = {}) {
  return request
    .get(`/documents/${type}/${id}/pdf${open ? '?inline=1' : ''}`)
    .then(async (r) => {
      if (!r.ok || (r.headers.get('content-type') || '').includes('application/json')) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || 'Could not produce the PDF.');
      }
      return r.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      if (open) {
        window.open(url, '_blank', 'noopener');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `${type}-${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    })
    .catch((e) => notification.error({ message: e.message }));
}

export function PdfButton({ type, id, size, children = 'PDF', fileName }) {
  return (
    <Button size={size} icon={<FilePdfOutlined />} onClick={() => fetchDocumentPdf(type, id, { open: true, fileName })} title="Open as PDF">
      {children}
    </Button>
  );
}

/**
 * "E-mail to client" button + compose modal with the delivery log. `type`
 * is invoice | engagement-letter | proposal. onSent fires after a successful
 * send so the page can refetch (a draft letter / pending proposal becomes SENT).
 */
export function EmailButton({ type, id, size, onSent, children = 'E-mail', disabled }) {
  const [open, setOpen] = React.useState(false);
  const [defaults, setDefaults] = React.useState(null);
  const [deliveries, setDeliveries] = React.useState([]);
  const [sending, setSending] = React.useState(false);
  const [form] = Form.useForm();

  const load = () => {
    Promise.all([
      request.get(`/documents/${type}/${id}/email-defaults`).then((r) => r.json()),
      request.get(`/documents/${type}/${id}/deliveries`).then((r) => r.json()),
    ]).then(([d, log]) => {
      if (d.status) {
        setDefaults(d.data);
        form.setFieldsValue({ to: d.data.to || '', subject: d.data.subject, message: d.data.message });
      } else {
        notification.error({ message: d.message });
      }
      if (log.status) setDeliveries(log.data);
    });
  };

  const openModal = () => {
    setOpen(true);
    load();
  };

  const send = () => {
    form.validateFields().then((values) => {
      setSending(true);
      request
        .post(`/documents/${type}/${id}/email`, values)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: r.message });
            setOpen(false);
            if (onSent) onSent();
          } else {
            notification.error({ message: r.message });
            load();
          }
        })
        .finally(() => setSending(false));
    });
  };

  return (
    <>
      <Button size={size} icon={<MailOutlined />} onClick={openModal} disabled={disabled} title="E-mail as PDF">
        {children}
      </Button>
      <Modal title="E-mail document" open={open} onCancel={() => setOpen(false)} onOk={send} okText="Send" confirmLoading={sending} okButtonProps={{ disabled: !!defaults?.blocker || defaults?.configured === false }} width={720}>
        {defaults?.blocker && <Alert type="warning" showIcon message={defaults.blocker} style={{ marginBottom: 12 }} />}
        {defaults && defaults.configured === false && <Alert type="error" showIcon message="E-mail is not configured on this server. Ask your administrator to set the SMTP settings." style={{ marginBottom: 12 }} />}
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="to" label="To" rules={[{ required: true }]} style={{ width: 330 }}>
              <Input placeholder="client@example.com" />
            </Form.Item>
            <Form.Item name="cc" label="Cc" style={{ width: 330 }}>
              <Input placeholder="optional" />
            </Form.Item>
          </Space>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="message" label="Message" tooltip="The PDF is attached automatically">
            <Input.TextArea rows={7} />
          </Form.Item>
        </Form>
        <Typography.Text strong>Delivery log</Typography.Text>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          style={{ marginTop: 6 }}
          dataSource={deliveries}
          locale={{ emptyText: 'Not sent yet' }}
          columns={[
            { title: 'When', dataIndex: 'sent_at', width: 140, render: (v) => moment(v).format('YYYY-MM-DD HH:mm') },
            { title: 'To', dataIndex: 'to_address' },
            { title: 'By', key: 'by', width: 140, render: (_, d) => d.SentBy?.name || '-' },
            { title: 'Result', key: 'st', width: 200, render: (_, d) => (d.status === 'SENT' ? <Tag color="green">SENT</Tag> : <Tag color="red" title={d.error}>FAILED - {d.error}</Tag>) },
          ]}
        />
      </Modal>
    </>
  );
}
